import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./loginScreen.module.css";
import { PublicFooter } from '../ui/footer/publicFooter.component';
import { Box, Button, Card, CardActionArea, CardContent, FormControl, IconButton, Link, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuthContext } from "../../contexts/auth";
import { useTheme } from "../../contexts/theme";
import { LoginResponse, TenantData, TokenRequest } from "../../types/auth";
import ApartmentIcon from '@mui/icons-material/Apartment';
import BiometricIcon from '@mui/icons-material/Fingerprint';
import { browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from "@simplewebauthn/browser";
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import { Trans, useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { ERROR_SNACKBAR } from "../../consts";

export const LoginScreen = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const {
    getLoginCode,
    login,
    loginWithWebAuthn,
    loginWithGoogle,
    logout,
    setTenant,
    loadingState,
    errorState,
    isSecure,
  } = useAuthContext();

  const defaultLoginData = { email: "", pin_code: "" };
  const [loginData, setLoginData] = useState(defaultLoginData);
  const [tenants, setTenants] = useState<TenantData[]>([]);
  const [loginCodeSent, setLoginCodeSent] = useState<boolean>(false);
  const [webauthnAvailable, setWebauthnAvailable] = useState<boolean>(false);
  const [tokenRequestData, setTokenRequestData] = useState<TokenRequest>({
    email: "",
    tenant_selection_pin_code: "",
  });

  useEffect(() => {
    if (!browserSupportsWebAuthn()) return;
    platformAuthenticatorIsAvailable().then(setWebauthnAvailable);
  }, []);

  const handleLoginResponse = (data: LoginResponse | undefined) => {
    if (!data) return;
    const tokenRequest = {
      email: data.email,
      tenant_selection_pin_code: data.tenant_selection_code,
    };
    if (data.tenants.length === 1) {
      // Only one workspace available, skip the selection step.
      setTenant(tokenRequest, data.tenants[0].id);
      return;
    }
    setTenants(data.tenants);
    setTokenRequestData(tokenRequest);
  };
  const handleLoginSubmit = async () => {
    const { success } = await getLoginCode({ email: loginData.email });
    if (success) setLoginCodeSent(true);
  };
  const handlePinCodeSubmit = async () => {
    const { data } = await login(loginData);
    handleLoginResponse(data);
  };
  const handleWebAuthnLogin = async () => {
    const { data } = await loginWithWebAuthn(loginData.email);
    handleLoginResponse(data);
  };
  const handleGoogleLogin = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) return;
    const { data } = await loginWithGoogle(credentialResponse.credential);
    handleLoginResponse(data);
  };
  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };
  const handleLogout = () => {
    setTenants([]);
    setLoginCodeSent(false);
    setLoginData(defaultLoginData);
    logout();
  };

  const handleTenantSelect = (tenantId: string) => setTenant(tokenRequestData, tenantId);

  const renderHeader = (title: string, titleClassName: string = styles.headerTitle) => (
    <div className={styles.headerRow}>
      <IconButton
        className={styles.backButton}
        onClick={() => navigate('/')}
        aria-label={t("Go back")}
        size="small"
      >
        <ArrowBackIcon fontSize="small" />
      </IconButton>
      <div className={titleClassName}>{title}</div>
    </div>
  );

  const renderLoginContent = () => (
    <FormControl variant="standard" className={styles.formControl} fullWidth>
      {renderHeader(t("Log in"))}
      <form action={handleLoginSubmit}>
        <Box className={styles.inputRow}>
          <Box sx={{ width: '100%' }}>
            <TextField name="email" type="email" label={t("Email address")} size="small" sx={{ width: '100%' }} value={loginData.email} onChange={handleLoginChange} required />
            {errorState.login?.email && (
              <div className={styles.errorMessage}>{errorState.login.email[0].message}</div>
            )}
          </Box>
          <Button
            variant="contained"
            color="success"
            type="submit"
            onClick={handleLoginSubmit}
            loading={loadingState.login}
            fullWidth
          >
            {t("Send pin code")}
          </Button>
        </Box>
      </form>
      {webauthnAvailable && (
        <Button
          variant="outlined"
          color="success"
          type="button"
          startIcon={<BiometricIcon />}
          onClick={handleWebAuthnLogin}
          loading={loadingState.webauthn}
          disabled={!loginData.email}
          fullWidth
        >
          {t("Biometric login")}
        </Button>
      )}
      <div className={styles.divider}>{t("or")}</div>
      <Box className={styles.googleButtonRow}>
        <GoogleLogin
          onSuccess={handleGoogleLogin}
          onError={() => enqueueSnackbar(t("Google sign-in failed. Please try again."), ERROR_SNACKBAR)}
          theme={isDarkMode ? "filled_black" : "outline"}
          text="signin_with"
        />
      </Box>
    </FormControl>
  );

  const renderPinCodeContent = () => (
    <form action={handlePinCodeSubmit}>
      <FormControl variant="standard" className={styles.formControl} fullWidth>
        {renderHeader(t("Enter pin code from your email"))}
        <Box className={styles.inputRow}>
          <Box sx={{ width: "100%" }}>
            <TextField
              name="pin_code"
              type="text"
              label={t("Pin code")}
              size="small"
              sx={{ width: '100%' }}
              value={loginData.pin_code}
              onChange={handleLoginChange}
              required
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }
              }}
            />
            {errorState.login?.pin_code && (
              <div className={styles.errorMessage}>{errorState.login.pin_code[0].message}</div>
            )}
          </Box>
          <Button
            variant="contained"
            color="success"
            type="submit"
            onClick={handlePinCodeSubmit}
            loading={loadingState.login}
            fullWidth
          >
            {t("Submit login")}
          </Button>
        </Box>
      </FormControl>
    </form>
  );

  const renderTenantContent = () => {
    return (
      <>
        {renderHeader(t("Select Workspace"), styles.resetHeaderTitle)}
        <p>{t("You have more than one available workspace. Please select one.")}</p>
        <FormControl variant="standard" className={styles.formControl}>
          {tenants.map((tenant, key) => (
            <Card key={key}>
              <CardActionArea onClick={() => handleTenantSelect(tenant.id)}>
                <CardContent className={styles.cardContent}>
                  <Box className={styles.logoBox}>
                    { tenant.logo ? (
                      <img src={tenant.logo} alt="" />
                      ) : <ApartmentIcon fontSize="large" />
                    }
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" lineHeight={1} component="div">
                      {tenant.name}
                    </Typography>
                    {tenant.website && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tenant.website}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
            <p className={styles.logoutParagraph}>
              <Trans
                i18nKey="Want to use another account? <clickHereLink>Click here</clickHereLink> to logout."
                components={{
                  clickHereLink: <Link href="#" onClick={() => handleLogout() } />,
                }}
              />
            </p>
        </FormControl>
      </>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.loginBox}>
        {tenants.length > 0
          ? renderTenantContent()
          : loginCodeSent
          ? renderPinCodeContent()
          : !isSecure
          ? renderLoginContent()
          : null}
      </Box>
      <PublicFooter />
    </Box>
  );
};
